<template>
  <div id="calc" class="container container-fluid qrs-typography" @click.stop.prevent="handleClick">
    <p>The <strong>4AT</strong> is a brief tool for <strong>delirium</strong> detection designed for
      use in clinical practice. It is the standard tool for delirium detection in many countries. It
      is recommended in multiple guidelines and pathways.</p>
    <p>It incorporates the Months Backwards test and the Abbreviated Mental Test - 4 (AMT4), which
      are short tests for cognitive impairment. This provides basic cognitive testing, aimed at
      detecting moderate-severe cognitive impairment, alongside assessment for delirium. See <a
        href="https://www.the4at.com/guide" target="_blank">https://www.the4at.com/guide</a>.</p>

    <Info />

    <div class="row " v-if="errormessage.length > 0">
      <div class="col-xs-12">
        <div id="error" class="alert alert-calc alert-danger txt-danger" v-html="errormessage">
        </div>
      </div>
    </div>

    <div class="questions__intro-text">
      <div class="row">
        <div class="col-xs-12">
          <p>Please click on the appropriate response to each question, then click on “Calculate”
          </p>
        </div>
      </div>
    </div>

    <div class="questions">
      <div class="row">
        <div class="col-xs-12 col-md-6">
          <span><strong>Question</strong></span>
        </div>
        <div class="col-xs-12 col-md-6">
          <span><strong>Please select your answer</strong></span>
        </div>
      </div>
    </div>

    <div class="question" v-for="q in questions" :key="q.id">
      <div class="row">
        <div class="col-xs-12 col-md-6">
          <h3 class="txt--xl mb-1 mb--1">{{ q.title }}</h3>
          <p v-html="q.info"></p>
        </div>

        <div class="col-xs-12 col-md-6">
          <div class="list-group">
            <div v-for="o in q.options" :key="o.id">
              <button @click="optionclicked(q.id, o.id)"
                @keydown.enter="optionclicked(event, q.id, o.id)" type="button"
                v-bind:class="{ 'list-group-item list-group-item-action d-flex justify-content-between align-items-center mb-1 active': (o.selected), 'list-group-item list-group-item-action d-flex justify-content-between align-items-center mb-1': (!o.selected) }">
                {{ o.text }}
                <span class="badge badge-success">{{ o.value_label }}</span>
              </button>
            </div>
          </div>
        </div>

      </div>
      <hr />
    </div>

    <div class="row">
      <div class="col-xs-12">
        <div class="calculator__result bg-secondary bg--secondary p--2 p-2 mb--2 mb-2">
          <div id="calculator-copy" class="color--white text-white mb-1 mb--1" v-if="displayresult">
            <div id="result-response" v-if="isvalid"></div>
            <h3 class="txt--xl">Total 4AT Score : {{ result.score }}</h3>
            <p v-if="result.text.length > 0"><strong>Result:</strong> {{ result.text }}</p>

            <table class="table" v-if="result.calctext.length > 0" v-html="result.calctext"></table>
          </div>
          <div class="control-holder">
            <button class="btn btn-primary mr-1 mr--1" @click="calculateclicked" type="button"
              v-if="!displayresult">Calculate</button>
            <button class="btn btn-primary mr-1 mr--1" id="resetButton" type="button" @click="reset"
              v-if="displayresult">Reset</button>
            <button class="btn btn-primary mr-1 mr--1" id="copyButton" type="button"
              @click="copyToClipboard" v-if="displayresult">Copy Result</button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-xs-12">
        <div id="howtointerpret">
          <h3>How to interpret AMT4</h3>
          <p>AMT4 is not a full cognitive test and decisions should not be made on this basis. A
            high AMT4 score can indicate general cognitive impairment, but note that a low score
            does not rule out mild cognitive impairment.</p>
        </div>
        <div id="howtoscore">
          <h3>How to score acute change or fluctuating course</h3>
          <p>This item requires information from one or more sources – eg a GP letter, personal
            knowledge of the patient, key informant such as family member, deduction from other
            information in patient history (for example, if the patient is drowsy and usually
            functions independently, the clinician may decide that this indicates acute change). If
            you do not have sufficient information, leave this element blank and click on
            “Calculate.”</p>
        </div>

        <div id="regulatory">
          <h3>Regulatory Information</h3>
          <p>
            <strong>Product Name: </strong>Calculator Suite <br>
            <strong>Calculator Name: </strong>4AT - 4 A's Assessment test for delirium and cognitive
            impairment<br>
            <strong>Calculator Version: </strong> v2.1.0
          </p>
          <div class="row">
            <div class="col-xs-4 col-lg-2">
              <img src="/img/cemark/shii.png" alt="InnoScot Health" />
            </div>
            <div class="col-xs-8 col-lg-10">
              <p>
                Innoscot Health<br>
                Suite 23, The Pentagon Centre<br>
                36 Washington Street<br>
                Glasgow<br>
                G3 8AZ
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-xs-12">
              <p>
                <img src="/img/cemark/ukca.png" alt="UK Conformity Assessed" />
                <!-- <img src="/img/cemark/ce.png" alt="CE marking"/> -->
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-xs-4 col-lg-2">
              <img src="/img/cemark/warning.png" alt="InnoScot Health" />
            </div>
            <div class="col-xs-8 col-lg-10">
              <p>
                <strong>Calculators are <u>not</u> intended to be used by members of the public as a
                  substitute for professional advice.</strong>
              </p>
            </div>
          </div>

          <div class="row">
            <div class="col-xs-4 col-lg-2">
              <img src="/img/cemark/info.png" alt="More Information on Right Decision" />
            </div>
            <div class="col-xs-8 col-lg-10">
              <p>
                <a href="https://rightdecision.scot.nhs.uk/quality-assurance-and-regulation/calculator-suite-instructions-for-use.pdf"
                  target="_blank">Instructions for use</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Info from "@/components/Info.vue";

export default {
  name: "Calculator",
  components: {
    Info
  },
  mounted() {
    setTimeout(
      function () {
        let bodyhtml = document.querySelector("body").innerHTML;
        if (bodyhtml.includes("--INAPP--")) {
          this.isapp = true;
        }
      }.bind(this),
      500
    );
  },
  data() {
    return {
      publicPath: process.env.BASE_URL,
      questions: [
        {
          id: "q1",
          title: "1. Alertness",
          required: true,
          info:
            "<p>This includes patients who may be markedly drowsy (eg difficult to rouse and/or obviously sleepy during assessment) or agitated/hyperactive.</p><p><strong>Observe the patient:</strong><br />If asleep, attempt to wake with  speech or gentle touch on shoulder. Ask the patient to state their name and address to assist rating.</p>",
          options: [
            {
              id: "o1",
              text:
                "Normal (fully alert, but not agitated, throughout assessment)",
              value_label: "0",
              value: 0,
              selected: false
            },
            {
              id: "o2",
              text: "Mild sleepiness for <10 seconds after waking, then normal",
              value_label: "0",
              value: 0,
              selected: false
            },
            {
              id: "o3",
              text: "Clearly abnormal",
              value_label: "+4",
              value: 4,
              selected: false
            }
          ]
        },
        {
          id: "q2",
          title: "2. AMT4",
          required: true,
          info:
            "<p><strong>Ask the patient to state:</strong><br />Age, date of birth, place (name of the hospital or building), current year</p><p></p>",
          options: [
            {
              id: "o4",
              text: "No mistakes",
              value_label: "0",
              value: 0,
              selected: false
            },
            {
              id: "o5",
              text: "1 mistake",
              value_label: "+1",
              value: 1,
              selected: false
            },
            {
              id: "o6",
              text: "≥2 mistakes or untestable ",
              value_label: "+2",
              value: 2,
              selected: false
            }
          ]
        },
        {
          id: "q3",
          title: "3. Attention",
          required: true,
          info:
            "<p><strong>Ask the patient:</strong><br />&quot;Please tell me the months of the year in backwards order, starting at December.&quot;</p><p>To assist initial understanding one prompt of &quot;what is the month before December?&quot; is permitted.</p>",
          options: [
            {
              id: "o7",
              text: "Achieves ≥7 months correctly ",
              value_label: "0",
              value: 0,
              selected: false
            },
            {
              id: "o8",
              text: "Starts but scores <7 months / refuses to start",
              value_label: "+1",
              value: 1,
              selected: false
            },
            {
              id: "o9",
              text:
                "Untestable (cannot start because unwell, drowsy, inattentive)",
              value_label: "+2",
              value: 2,
              selected: false
            }
          ]
        },
        {
          id: "q4",
          title: "4. Acute change or fluctuating course",
          required: false,
          info:
            "<p>Evidence of significant change or fluctuation in: alertness, cognition, other mental function (eg paranoia, hallucinations) arising over the last 2 weeks and still evident in last 24hrs</p><p></p>",
          options: [
            {
              id: "o10",
              text: "No",
              value_label: "0",
              value: 0,
              selected: false
            },
            {
              id: "o11",
              text: "Yes",
              value_label: "+4",
              value: 4,
              selected: false
            }
          ]
        }
      ],
      result: {
        score: 0,
        calctext: "",
        text: "",
        subtext: ""
      },
      displayresult: false,
      errormessage: "",
      isvalid: false,
      answered_count: 0,
      answer_acutechange: false
    };
  },
  methods: {

    handleClick(e) {
      if (e.target.matches("a")) {
        let url = e.target.href;
        let target = e.target.target == "_blank" ? "_blank" : "_self";
        let dataId = e.target.dataset.id == null ? -1 : e.target.dataset.id;
        var isnativeapp = document.getElementById("isnativeapp");
        if (isnativeapp === undefined || isnativeapp === null) {
          window.open(url, target);
        }
        else {
          if (url.includes("#")) {
            window.Native('dataCallback', url);
          }
          if (target == "_blank") {
            let data = { title: '', nodeid: dataId, href: url, target: target };
            window.Native('dataCallback', data);
          }
        }
      }
    },
    reset() {
      this.result.score = 0;
      this.result.calctext = "";
      this.result.text = "";
      this.displayresult = false;
      this.showCopy = false;
      this.showform = false;
      this.errormessage = "";
      this.isvalid = false;
      this.answered_count = 0;
      this.answer_acutechange = false;
      this.questions.forEach(q => {
        q.options.forEach(o => {
          o.selected = false;
        });
      });
    },
    calculateclicked() {
      if (this.isvalid) {
        this.displayresult = true;
        this.showCopy = true;
      }

      if (!this.isvalid) {
        this.displayresult = false;
        this.errormessage = "<p><strong>One or more mandatory inputs missing.</strong></p>";
        this.errormessage += "<p>Mandatory inputs are: </p>";
        this.errormessage += "<ul style='margin:0'>";
        this.errormessage += "<li>Alertness</li>";
        this.errormessage += "<li>AMT4</li>";
        this.errormessage += "<li>Attention</li>";
        this.errormessage += "</ul>";
        this.errormessage += "<p>Please note that if the patient is unable to engage in the AMT4 or Attention items these should be scored as ‘untestable’ on the tool, that is, each given a score of 2.</p>";
        setTimeout(() => {
          const id = 'error';
          const yOffset = -100;
          const element = document.getElementById(id);
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 100);
      } else {
        this.errormessage = "";
      }
    },
    copyToClipboard() {
      var range;
      var finalText = "";
      if (document.selection) { // IE
        range = document.body.createTextRange();
        range.moveToElementText(document.getElementById('calculator-copy'));
        range.select();
        finalText = this.getDateToday() + "\n\n4AT assessment\n\n" + range + "\nEnter action taken as a result of this alert\n";
        document.selection.empty();
      } else if (window.getSelection) {
        range = document.createRange();
        range.selectNode(document.getElementById('calculator-copy'));
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        finalText = this.getDateToday() + "\n\n4AT assessment\n\n" + window.getSelection().toString() + "\nEnter action taken as a result of this alert";
        window.getSelection().removeAllRanges();
      }
      var dummy = document.createElement("textarea");
      // Add it to the document
      document.body.appendChild(dummy);

      // Set its ID
      dummy.setAttribute("id", "dummy_id");

      // Output the array into it
      document.getElementById("dummy_id").value = finalText;

      // Select it
      dummy.select();

      // Copy its contents
      document.execCommand("copy");

      // Remove it as its not needed anymore
      document.body.removeChild(dummy);

      var clicked = false;
      var copyButton = document.getElementById('copyButton');
      if (!clicked) {
        var last = copyButton.innerHTML;
        copyButton.innerHTML = 'Copied!';
        clicked = true;
        setTimeout(function () {
          copyButton.innerHTML = last;
          clicked = false;
        }.bind(copyButton), 5000);
      }
    },
    getDateToday() {
      var date = "";
      var today = new Date();
      date = today.getDate() + "/" + (today.getMonth() + 1) + "/" + today.getFullYear() + " " + (today.getHours() < 10 ? "0" + today.getHours() : today.getHours()) + ":" + (today.getMinutes() < 10 ? "0" + today.getMinutes() : today.getMinutes());
      return date;
    },
    closedclicked() {
      this.displayresult = false;
    },
    optionclicked(qid, oid) {
      this.displayresult = false;
      this.showCopy = false;
      let _score = 0;
      this.answered_count = 0;
      this.questions.forEach(q => {
        if (q.id === qid) {
          q.options.forEach(o => {
            if (o.id === oid) {
              if (o.selected) {
                o.selected = false;
              }
              else {
                o.selected = true;
              }

            } else {
              o.selected = false;
            }
          });
        }
      });
      let _cnt = 0;
      let _required_cnt = 0;
      let _required_answered_cnt = 0;
      this.answer_acutechange = false;
      this.questions.forEach(q => {
        if (q.required) {
          _required_cnt++;
        }
      });
      this.questions.forEach(q => {
        q.options.forEach(o => {
          if (o.selected) {
            _score += o.value;
            _cnt++;
          }
          if (o.selected && q.required) {
            _required_answered_cnt++;
          }
          if (q.id === "q4") {
            if (o.selected) {
              this.answer_acutechange = true;
            }
          }
        });
      });

      this.answered_count = _cnt;
      this.isvalid = _required_answered_cnt === _required_cnt;

      if (this.isvalid) {
        this.errormessage = "";
      }
      this.result.score = _score;

      if (_score === 0) {
        if (!this.answer_acutechange) {
          this.result.text = "Delirium or severe cognitive impairment unlikely. Delirium still possible as \"Acute Change or Fluctuating Course\" field incomplete.";
        }
        else {
          this.result.text = "Delirium or severe cognitive impairment unlikely.";
        }

      }
      if (_score > 0 && _score < 4) {
        if (!this.answer_acutechange) {
          this.result.text = "Possible cognitive impairment. Delirium still possible as \"Acute Change or Fluctuating Course\" field incomplete.";
        }
        else {
          this.result.text = "Possible cognitive impairment.";
        }
      }
      if (_score >= 4) {
        if (!this.answer_acutechange) {
          this.result.text = "Possible delirium +/- cognitive impairment. Note that \"Acute Change or Fluctuating Course\" field is incomplete so actual score may be higher.";
        }
        else {
          this.result.text = "Possible delirium +/- cognitive impairment.";
        }
      }



      let _calctext = "<tr><td width='80'><strong>Score</strong></td><td><strong>Questions</strong></td></tr>";
      let _idx2 = 0;
      //let _len = this.questions.length;
      this.questions.forEach(q => {
        //_calctext = _calctext + q.title+": ";

        q.options.forEach(o => {
          if (o.selected) {
            if (_idx2 === 0) {
              _calctext = _calctext + "<tr><td><span class='badge badge-success badge-pill'>" + o.value + "</span></td><td>" + q.title + "</td></tr>";
            } else {
              _calctext = _calctext + " <tr><td><span class='badge badge-success badge-pill'>" + o.value + "</span></td><td>" + q.title + "</td></tr>";
            }
          }
        });

        _idx2++;
      });

      this.result.calctext = _calctext;
    }
  }
};
</script>
